import React, { useRef, forwardRef } from "react";
import { Row, Col, NMForm, usePub, } from "@neumetric/blueprint-lib/components";
import { useSelector } from "react-redux";
import { getDepartments } from "@neumetric/blueprint-lib/store";
import { Collapse } from "antd";
import { useParams } from "react-router-dom";
import {
  getSeverityList,
  getAssetTypesList,
  getAssetsList,
  getSourcesByStatus,
  getVulnerabilitiesByStatus,
  getThreatsByStatus,
  getCategoryList,
  getCurrentRisk,
  getSeverityByScore,
} from "../../../store/mitigator";

const FormFields = [
  {
    label: "Context",
    attribute: "context",
    typeRepresentation: "TEXT",
    placeholder: "Context",
    labelAlign: "middle",
    defaultValue: "No Context available.",
    classes: { root: "w-100", label: "w-100-px" },
  },
  {
    label: "Consequence",
    attribute: "consequence",
    typeRepresentation: "TEXT",
    placeholder: "Consequence",
    labelAlign: "middle",
    defaultValue: "No Consequence available.",
    classes: { root: "w-100", label: "w-100-px" },
  },
  {
    label: "Department",
    attribute: "department",
    typeRepresentation: "DROPDOWN",
    placeholder: "Department",
    labelAlign: "middle",
    classes: { root: "w-40 mb-7", label: "w-100-px" },
  },
  {
    label: "Asset Type",
    attribute: "asset_type_id",
    typeRepresentation: "DROPDOWN",
    placeholder: "Asset Type",
    labelAlign: "middle",
    classes: { root: "w-55 mb-7", label: "w-93-px" },
  },
  {
    className: "flex-wrap justify-end w-100",
    childrenFields: [
      {
        label: "Asset",
        attribute: "asset_id",
        typeRepresentation: "DROPDOWN",
        placeholder: "Asset",
        labelAlign: "middle",
        classes: { root: "w-55 mb-7", label: "w-93-px" },
      },
      {
        label: "Threat",
        attribute: "threat_id",
        typeRepresentation: "DROPDOWN",
        placeholder: "Threat",
        labelAlign: "middle",
        classes: { root: "w-55 mb-7", label: "w-93-px" },
      },
      {
        label: "Vulnerability",
        attribute: "vulnerability_id",
        typeRepresentation: "DROPDOWN",
        placeholder: "Vulnerability",
        labelAlign: "middle",
        classes: { root: "w-55 mb-7", label: "w-93-px" },
      },
      {
        label: "Source",
        attribute: "source_id",
        typeRepresentation: "DROPDOWN",
        placeholder: "Source",
        labelAlign: "middle",
        classes: { root: "w-55 mb-7", label: "w-93-px" },
      },
      {
        label: "Category",
        attribute: "category",
        typeRepresentation: "DROPDOWN",
        placeholder: "Category",
        labelAlign: "middle",
        classes: { root: "w-55 mb-7", label: "w-93-px" },
      },
    ],
  },
];

const RatingFormFields = [
  {
    label: "Inherent Rating",
    attribute: "rating",
    typeRepresentation: "SCORE_GROUP",
    layout: "vertical",
    groups: [
      {
        label: "Confidentiality:",
        attribute: "confidentiality",
        classes: { label: "w-170-px" },
      },
      {
        label: "Integrity:",
        attribute: "integrity",
        classes: { label: "w-170-px" },
      },
      {
        label: "Availability:",
        attribute: "availability",
        classes: { label: "w-170-px" },
      },
      {
        label: "Security:",
        attribute: "security",
        classes: { label: "w-170-px" },
      },
      {
        label: "Privacy:",
        attribute: "privacy",
        classes: { label: "w-170-px" },
      },
    ],
    classes: { root: "w-100 mb-10", label: "fs-20 fw-600" },
  },
  {
    label: "Impact",
    attribute: "impact",
    typeRepresentation: "SCORE",
    classes: { root: "w-100 mb-4", label: "fs-20 fw-600 w-156-px" },
  },
  {
    label: "Probability",
    attribute: "probability",
    typeRepresentation: "SCORE",
    classes: { root: "w-100", label: "fs-20 fw-600 w-156-px" },
  },
  {
    label: "Score",
    attribute: "score",
    typeRepresentation: "SCORE",
    type: "PROGRESS",
    classes: { root: "w-100", label: "fs-22 fw-600 w-156-px" },
  },
];

export const InfoSection = forwardRef((props, ref) => {
  const publish = usePub();
  const { editMode, handleDecisionAutoUpdate } = props;
  const { riskId } = useParams();
  const currentRisk = useSelector(getCurrentRisk);
  const _ref = useRef(null);
  const _ratingRef = useRef(null);
  const departments = useSelector(getDepartments);
  const severityList = useSelector(getSeverityList);
  const assetTypesList = useSelector(getAssetTypesList);
  const assetsList = useSelector(getAssetsList);
  const sourcesList = useSelector(getSourcesByStatus.bind(null, "active"));
  const threatsList = useSelector(getThreatsByStatus.bind(null, "active"));
  const vulnerabilitiesList = useSelector(
    getVulnerabilitiesByStatus.bind(null, "active")
  );
  const categoryList = useSelector(getCategoryList);


  const getOptions = (attribute) => {
    if (attribute === "department") {
      return departments;
    }
    if (attribute === "asset_type_id") {
      return assetTypesList.map((assetType) => ({
        id: assetType.id,
        label: assetType.asset_type,
      }));
    }
    if (attribute === "asset_id") {
      return assetsList.map((asset) => ({
        id: asset.id,
        label: asset.asset,
      }));
    }
    if (attribute === "source_id") {
      return sourcesList.map((source) => ({
        id: source.id,
        label: source.name,
      }));
    }
    if (attribute === "vulnerability_id") {
      return vulnerabilitiesList.map((vulnerability) => ({
        id: vulnerability.id,
        label: vulnerability.vulnerability,
      }));
    }
    if (attribute === "threat_id") {
      return threatsList.map((threat) => ({
        id: threat.id,
        label: threat.threat,
      }));
    }
    if (attribute === "category") {
      return categoryList;
    }
    return [];
  };

  const formInitialValues = React.useMemo(() => {
    if (riskId && currentRisk) {
      return { ...currentRisk };
    }
    return {};
  }, [riskId, currentRisk]);

  const validate = () => {
    return (
      _ref.current.validate() &&
      _ratingRef.current.validate()
    );
  };

  const getMappedFields = React.useCallback((fields) => {
    return fields.map((field) => {
      const newField = { ...field };
      if (field.typeRepresentation === "DROPDOWN") {
        newField.placeholder = editMode ? newField.placeholder : "Undefined";
      }
      if (field.childrenFields) {
        newField.childrenFields = getMappedFields(field.childrenFields);
      }
      return newField;
    })
  }, [editMode])

  const getValues = () => {
    let ratingValues = _ratingRef.current.getValues();
    ratingValues = { ...(ratingValues.rating || {}), ...ratingValues };
    delete ratingValues.rating;
    return {
      ..._ref.current.getValues(),
      ...ratingValues,
    };
  };

  React.useImperativeHandle(ref, () => ({
    validate: validate,
    getValues: getValues,
  }));

  const handleChange = (attribute, value) => {
    const values = _ratingRef.current.getValues();
    const newValues = {};
    publish("NM_OBJECT_HEADER:CHANGE");
    const updateDecision = (score) => {
      if (score > -1) {
        const decision = score <= 3 ? "acceptable" : "treat";
        const status = score <= 3 ? "accepted" : "pending";
        const severity = getSeverityByScore(severityList, score);
        handleDecisionAutoUpdate({
          decision,
          risk_status: status,
          severity,
        })
      }
    };
    if (attribute === "rating") {
      let impact = Object.values(value || {}).reduce((a, b) => {
        if (b > -1) {
          return a + b;
        }
        return a;
      }, 0);
      if (impact > -1) {
        impact = Math.round(impact / 5);
        let score = values.score;
        if (values.probability > -1) {
          score = Math.round((impact * values.probability) / 5);
        }
        newValues.impact = impact;
        newValues.score = score;
        updateDecision(score);
      }
    }
    if (attribute === "probability") {
      const score = Math.round((values.impact * value) / 5);
      if (score > -1) {
        newValues.score = score;
        updateDecision(score);
      }
    }
    if (attribute === "score") {
      updateDecision(value);
    }
    if (Object.keys(newValues).length > 0) {
      _ratingRef.current.updateFormData(newValues);
    }
  };
  const header = (
    <Row align="center" justify="between" className="w-100">
      <span className="ff-exo2 fs-18 fw-600 line-28 text-filled">
        Information
      </span>
    </Row>
  );
  return (
    <Collapse
      className="w-100 mb-16"
      defaultActiveKey={["info"]}
      collapsible="disabled"
    >
      <Collapse.Panel key="info" header={header} showArrow={false}>
        <Row className="w-100" align="start">
          <Col className="w-50 mr-16">
            <NMForm
              ref={_ref}
              fields={getMappedFields(FormFields)}
              formData={formInitialValues}
              getOptions={getOptions}
              readOnly={!editMode}
              onChange={() => publish("NM_OBJECT_HEADER:CHANGE")}
            />
          </Col>
          <Col className="w-50 ml-16">
            <NMForm
              ref={_ratingRef}
              onChange={handleChange}
              fields={RatingFormFields}
              formData={formInitialValues}
              readOnly={!editMode}
              className="w-350-px"
            />
          </Col>
        </Row>
      </Collapse.Panel>
    </Collapse>
  );
});
